import React, { useState, useEffect } from 'react';
import { X, Euro, Calendar, Building, Tag, FileText, Info, Check, Copy, Repeat, Zap } from 'lucide-react';
import { Expense, ExpenseType, ExpenseStatus, Property, PropertyAsset, Agency } from '../../types';
import { storage } from '../../services/storage';
import { useModal } from '../ui/ModalProvider';
import { SearchableSelect } from '../ui/SearchableSelect';

interface AddExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (expense: Expense) => Promise<void>;
  propertyId?: string;
  properties?: Property[];
  agencyId?: string;
  year?: number;
  month?: number;
  expenseToEdit?: Expense | null;
}

const EXPENSE_TYPES: { value: ExpenseType; label: string; icon: string; commonCategories: string[] }[] = [
  { value: 'RENT', label: 'Loyers', icon: '🏠', commonCategories: ['Loyer'] },
  { value: 'CHARGES', label: 'Charges', icon: '⚡', commonCategories: ['Forfait de charges', 'Eau', 'Electricité/gaz', 'Internet', 'Assurance', 'Taxe foncière', 'Charges de copropriété', 'Ordures ménagères'] },
  { value: 'CLEANING', label: 'Ménage', icon: '🧹', commonCategories: ['Ménage', 'Blanchisserie'] },
  { value: 'MAINTENANCE', label: 'Entretien', icon: '🔧', commonCategories: ['Aménagement entretien', 'Réparation', 'Entretien'] },
  { value: 'INSURANCE', label: 'Assurance', icon: '🛡️', commonCategories: ['Assurance Habitation'] },
  { value: 'UTILITIES', label: 'Utilities', icon: '💧', commonCategories: ['Eau', 'Electricité/gaz', 'Internet'] },
  { value: 'SOFTWARE', label: 'Logiciels', icon: '💻', commonCategories: ['Logiciel de gestion', 'Logiciel comptable', 'Autres logiciels'] },
  { value: 'OTHER', label: 'Autres', icon: '📋', commonCategories: ['Autres'] }
];

const CATEGORY_MAP: Record<ExpenseType, string[]> = {
  RENT: ['Loyer'],
  CHARGES: ['Forfait de charges', 'Eau', 'Electricité/gaz', 'Internet', 'Assurance', 'Taxe foncière', 'Charges de copropriété', 'Ordures ménagères'],
  CLEANING: ['Ménage', 'Blanchisserie'],
  MAINTENANCE: ['Aménagement entretien', 'Réparation', 'Entretien', 'Maintenance générale'],
  INSURANCE: ['Assurance Habitation', 'Assurance PNO'],
  UTILITIES: ['Eau', 'Electricité/gaz', 'Internet'],
  SOFTWARE: ['Logiciel de gestion', 'Logiciel comptable', 'Autres logiciels'],
  PLACID_COMMISSION: ['Cotisation Placid'],
  OTHER: ['Autres']
};

export const AddExpenseModal: React.FC<AddExpenseModalProps> = ({
  isOpen,
  onClose,
  onSave,
  propertyId,
  properties = [],
  agencyId,
  year,
  month,
  expenseToEdit
}) => {
  const { toast } = useModal();
  const now = new Date();
  const currentYear = year || now.getFullYear();
  const currentMonth = month || now.getMonth() + 1;

  const [formData, setFormData] = useState<Partial<Expense>>({
    expenseType: 'OTHER',
    category: '',
    description: '',
    amount: 0,
    currency: 'EUR',
    year: currentYear,
    month: currentMonth,
    propertyId: propertyId || null,
    agencyId: agencyId,
    status: 'PENDING',
    isSoftwareExpense: false,
    isPlacidCommission: false,
    allocatedProperties: [],
    allocationMethod: 'EQUAL'
  });

  const [selectedPropertyAssets, setSelectedPropertyAssets] = useState<PropertyAsset[]>([]);
  const [availableAssets, setAvailableAssets] = useState<PropertyAsset[]>([]);
  const [recentExpenses, setRecentExpenses] = useState<Expense[]>([]);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Charger les dépenses récentes pour suggestions
  useEffect(() => {
    if (isOpen && !expenseToEdit) {
      loadRecentExpenses();
    }
  }, [isOpen, formData.expenseType, formData.category]);

  const loadRecentExpenses = async () => {
    try {
      const filters: any = {
        expenseType: formData.expenseType,
        limit: 5
      };
      if (formData.category) {
        // On va filtrer côté client
      }
      const expenses = await storage.getExpenses(filters);
      // Filtrer par catégorie si spécifiée
      const filtered = formData.category 
        ? expenses.filter(e => e.category === formData.category)
        : expenses;
      setRecentExpenses(filtered.slice(0, 5));
    } catch (error) {
      // Ignore
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (expenseToEdit) {
        setFormData(expenseToEdit);
        setShowAdvanced(true);
      } else {
        setFormData({
          expenseType: 'OTHER',
          category: '',
          description: '',
          amount: 0,
          currency: 'EUR',
          year: currentYear,
          month: currentMonth,
          propertyId: propertyId || null,
          agencyId: agencyId,
          status: 'PENDING',
          isSoftwareExpense: false,
          isPlacidCommission: false,
          allocatedProperties: [],
          allocationMethod: 'EQUAL'
        });
        setShowAdvanced(false);
      }
      
      // Charger les assets si un bien est sélectionné
      if (propertyId && properties.length > 0) {
        const property = properties.find(p => p.id === propertyId);
        if (property && property.assets) {
          setAvailableAssets(property.assets);
        }
      }
    }
  }, [isOpen, expenseToEdit, propertyId, properties, currentYear, currentMonth, agencyId]);

  if (!isOpen) return null;

  const handlePropertyChange = (propId: string | null) => {
    const property = properties.find(p => p.id === propId);
    setFormData({ ...formData, propertyId: propId });
    if (property && property.assets) {
      setAvailableAssets(property.assets);
    } else {
      setAvailableAssets([]);
    }
  };

  const handleExpenseTypeChange = (type: ExpenseType) => {
    const categories = CATEGORY_MAP[type] || [];
    const typeInfo = EXPENSE_TYPES.find(t => t.value === type);
    
    setFormData({
      ...formData,
      expenseType: type,
      category: categories[0] || '',
      isSoftwareExpense: type === 'SOFTWARE',
      isPlacidCommission: type === 'PLACID_COMMISSION',
      propertyId: type === 'SOFTWARE' ? null : formData.propertyId
    });
    
    // Charger les dépenses récentes pour suggestions
    setTimeout(() => loadRecentExpenses(), 100);
  };

  const handleCopyFromRecent = (expense: Expense) => {
    setFormData({
      ...formData,
      expenseType: expense.expenseType,
      category: expense.category,
      description: expense.description || '',
      amount: expense.amount,
      supplierName: expense.supplierName,
      supplierContact: expense.supplierContact,
      propertyAssetReference: expense.propertyAssetReference
    });
    toast.success('Dépense copiée depuis l\'historique');
  };

  const handleDuplicateForNextMonth = async () => {
    if (!formData.expenseType || !formData.category || !formData.amount) {
      toast.error('Veuillez d\'abord remplir les informations de base');
      return;
    }

    const nextMonth = formData.month === 12 ? 1 : (formData.month || 0) + 1;
    const nextYear = formData.month === 12 ? (formData.year || 0) + 1 : formData.year;

    try {
      await onSave({
        ...formData,
        year: nextYear,
        month: nextMonth,
        id: undefined
      } as Expense);
      toast.success(`Dépense créée pour ${months.find(m => m.value === nextMonth)?.label} ${nextYear}`);
      onClose();
    } catch (error: any) {
      toast.error(`Erreur: ${error.message || 'Erreur inconnue'}`);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.expenseType || !formData.category || !formData.amount || formData.amount <= 0) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (formData.isSoftwareExpense && (!formData.allocatedProperties || formData.allocatedProperties.length === 0)) {
      toast.error('Veuillez sélectionner au moins un bien pour les dépenses logiciels');
      return;
    }

    try {
      const expense = formData as Expense;
      
      // Si dépense récurrente, forcer month et year à null
      if (expense.isRecurring) {
        expense.year = null;
        expense.month = null;
        expense.isRecurring = true;
      }
      
      await onSave(expense);
      
      if (expense.isRecurring) {
        toast.success('Dépense récurrente créée. Elle sera automatiquement incluse dans tous les mois.');
      } else {
        toast.success('Dépense enregistrée avec succès');
      }
      
      onClose();
    } catch (error: any) {
      toast.error(`Erreur lors de l'enregistrement: ${error.message || 'Erreur inconnue'}`);
    }
  };

  const months = [
    { value: 1, label: 'Janvier' },
    { value: 2, label: 'Février' },
    { value: 3, label: 'Mars' },
    { value: 4, label: 'Avril' },
    { value: 5, label: 'Mai' },
    { value: 6, label: 'Juin' },
    { value: 7, label: 'Juillet' },
    { value: 8, label: 'Août' },
    { value: 9, label: 'Septembre' },
    { value: 10, label: 'Octobre' },
    { value: 11, label: 'Novembre' },
    { value: 12, label: 'Décembre' }
  ];

  const availableCategories = CATEGORY_MAP[formData.expenseType || 'OTHER'] || [];
  const selectedTypeInfo = EXPENSE_TYPES.find(t => t.value === formData.expenseType);
  const suggestedExpense = recentExpenses[0];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-slate-200 bg-white sticky top-0 z-10">
          <div>
            <h2 className="text-xl font-bold text-slate-900 flex items-center">
              <Euro className="w-5 h-5 mr-2 text-amber-500" />
              {expenseToEdit ? 'Modifier la dépense' : 'Nouvelle dépense'}
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              {formData.propertyId 
                ? properties.find(p => p.id === formData.propertyId)?.name || properties.find(p => p.id === formData.propertyId)?.address
                : 'Dépense globale'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Suggestion rapide depuis dépenses récentes */}
          {!expenseToEdit && suggestedExpense && formData.expenseType && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center text-blue-900 font-medium mb-1">
                    <Zap className="w-4 h-4 mr-2" />
                    Suggestion rapide
                  </div>
                  <p className="text-sm text-blue-700 mb-2">
                    Dernière dépense "{suggestedExpense.category}" : {suggestedExpense.amount.toFixed(2)} € ({months.find(m => m.value === suggestedExpense.month)?.label} {suggestedExpense.year})
                  </p>
                  <button
                    type="button"
                    onClick={() => handleCopyFromRecent(suggestedExpense)}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center"
                  >
                    <Copy className="w-3 h-3 mr-1" />
                    Utiliser ces informations
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Section 1: Informations essentielles */}
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
            <div className="flex items-center mb-4">
              <div className="w-6 h-6 rounded-full bg-amber-500 text-white flex items-center justify-center text-xs font-bold mr-2">1</div>
              <h3 className="text-sm font-bold text-slate-700 uppercase">Informations essentielles</h3>
            </div>

            <div className="space-y-4">
              {/* Type et Catégorie - Grid avec icônes */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-500 mb-1 block uppercase">Type de dépense *</label>
                  <SearchableSelect
                    options={EXPENSE_TYPES.filter(t => t.value !== 'PLACID_COMMISSION')}
                    value={formData.expenseType || ''}
                    onChange={(value) => handleExpenseTypeChange(value as ExpenseType)}
                    placeholder="Sélectionner un type"
                    icon={<Tag className="w-4 h-4" />}
                    searchPlaceholder="Rechercher un type..."
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 mb-1 block uppercase">Catégorie *</label>
                  <SearchableSelect
                    options={availableCategories.map(cat => ({ value: cat, label: cat }))}
                    value={formData.category || ''}
                    onChange={(value) => {
                      setFormData({ ...formData, category: value });
                      setTimeout(() => loadRecentExpenses(), 100);
                    }}
                    placeholder="Sélectionner une catégorie"
                    icon={<Tag className="w-4 h-4" />}
                    searchPlaceholder="Rechercher une catégorie..."
                  />
                </div>
              </div>

              {/* Période et Montant - Ligne compacte */}
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-500 mb-1 block uppercase">Année *</label>
                  <input
                    type="number"
                    required
                    min="2020"
                    max="2100"
                    className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white text-slate-900 text-sm"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 mb-1 block uppercase">Mois *</label>
                  <SearchableSelect
                    options={months}
                    value={formData.month?.toString() || ''}
                    onChange={(value) => setFormData({ ...formData, month: parseInt(value) })}
                    placeholder="Mois"
                    icon={<Calendar className="w-4 h-4" />}
                    searchPlaceholder="Rechercher..."
                    showAllOnOpen={true}
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-xs font-bold text-slate-500 mb-1 block uppercase">Montant (€) *</label>
                  <div className="relative">
                    <Euro className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                    <input
                      type="number"
                      required
                      min="0"
                      step="0.01"
                      className="w-full pl-9 p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white text-slate-900 text-lg font-medium"
                      value={formData.amount || 0}
                      onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Bien concerné */}
          {!formData.isPlacidCommission && (
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
              <div className="flex items-center mb-4">
                <div className="w-6 h-6 rounded-full bg-amber-500 text-white flex items-center justify-center text-xs font-bold mr-2">2</div>
                <h3 className="text-sm font-bold text-slate-700 uppercase">Bien concerné</h3>
              </div>

              {formData.isSoftwareExpense ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <label className="text-xs font-bold text-amber-700 mb-2 block uppercase">Biens concernés *</label>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {properties.filter(p => p.status === 'MANAGED').map(prop => (
                      <label key={prop.id} className="flex items-center space-x-2 cursor-pointer p-2 hover:bg-amber-100 rounded">
                        <input
                          type="checkbox"
                          checked={(formData.allocatedProperties || []).includes(prop.id!)}
                          onChange={(e) => {
                            const current = formData.allocatedProperties || [];
                            if (e.target.checked) {
                              setFormData({ ...formData, allocatedProperties: [...current, prop.id!] });
                            } else {
                              setFormData({ ...formData, allocatedProperties: current.filter(id => id !== prop.id) });
                            }
                          }}
                          className="rounded border-slate-300 text-amber-500 focus:ring-amber-500"
                        />
                        <span className="text-sm text-slate-900 font-medium">{prop.name || prop.address}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-amber-600 mt-2">
                    {formData.allocatedProperties?.length || 0} bien(s) sélectionné(s)
                  </p>
                </div>
              ) : (
                <SearchableSelect
                  options={[
                    { value: '', label: 'Aucun (dépense globale)' },
                    ...properties.map(p => ({ value: p.id!, label: p.name || p.address }))
                  ]}
                  value={formData.propertyId || ''}
                  onChange={(value) => handlePropertyChange(value || null)}
                  placeholder="Sélectionner un bien (optionnel)"
                  icon={<Building className="w-4 h-4" />}
                  searchPlaceholder="Rechercher un bien..."
                />
              )}
            </div>
          )}

          {/* Section 3: Dépense récurrente */}
          {(formData.expenseType === 'RENT' || formData.expenseType === 'CHARGES' || formData.expenseType === 'UTILITIES') && !expenseToEdit && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="isRecurring"
                  checked={formData.isRecurring || false}
                  onChange={(e) => {
                    const isRecurring = e.target.checked;
                    setFormData({ 
                      ...formData, 
                      isRecurring,
                      // Si récurrente, on garde year/month pour le contexte mais on les mettra à null à la sauvegarde
                      // Si pas récurrente, on garde les valeurs
                    });
                  }}
                  className="mt-1 w-5 h-5 text-amber-500 rounded focus:ring-amber-500 border-gray-300"
                />
                <div className="flex-1">
                  <label htmlFor="isRecurring" className="text-sm font-bold text-blue-900 cursor-pointer block">
                    Dépense récurrente mensuelle (ex: Loyer mensuel)
                  </label>
                  <p className="text-xs text-blue-700 mt-1">
                    Cette dépense sera automatiquement incluse dans les calculs de tous les mois. Elle apparaîtra comme une dépense mensuelle récurrente. Vous pourrez la modifier pour un mois spécifique si elle varie.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Section 4: Détails (optionnel, replié par défaut) */}
          <div>
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center justify-between w-full text-left text-sm font-medium text-slate-600 hover:text-slate-900 p-2 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <span className="flex items-center">
                <Info className="w-4 h-4 mr-2" />
                Détails supplémentaires (optionnel)
              </span>
              <span className="text-xs text-slate-400">{showAdvanced ? 'Masquer' : 'Afficher'}</span>
            </button>

            {showAdvanced && (
              <div className="mt-3 bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-4">
                {/* Description */}
                <div>
                  <label className="text-xs font-bold text-slate-500 mb-1 block uppercase">Description</label>
                  <textarea
                    className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white text-slate-900 text-sm"
                    rows={2}
                    value={formData.description || ''}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Détails supplémentaires..."
                  />
                </div>

                {/* Lien avec PropertyAsset */}
                {(formData.expenseType === 'CHARGES' || formData.expenseType === 'UTILITIES' || formData.expenseType === 'INSURANCE') && formData.propertyId && availableAssets.length > 0 && (
                  <div>
                    <label className="text-xs font-bold text-slate-500 mb-1 block uppercase">Lier à un asset (optionnel)</label>
                    <SearchableSelect
                      options={[
                        { value: '', label: 'Aucun lien' },
                        ...availableAssets.map(asset => ({ value: asset.id, label: `${asset.provider} - ${asset.contractNumber}` }))
                      ]}
                      value={formData.propertyAssetReference || ''}
                      onChange={(value) => {
                        const selectedAsset = availableAssets.find(a => a.id === value);
                        setFormData({ 
                          ...formData, 
                          propertyAssetReference: value || null,
                          // Préremplir le fournisseur si un asset est sélectionné
                          supplierName: selectedAsset?.provider || formData.supplierName || '',
                          supplierContact: selectedAsset?.contractNumber || formData.supplierContact || ''
                        });
                      }}
                      placeholder="Sélectionner un asset"
                      icon={<FileText className="w-4 h-4" />}
                      searchPlaceholder="Rechercher un asset..."
                    />
                  </div>
                )}

                {/* Fournisseur */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-bold text-slate-500 mb-1 block uppercase">Nom du fournisseur</label>
                    <input
                      type="text"
                      className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white text-slate-900 text-sm"
                      value={formData.supplierName || ''}
                      onChange={(e) => setFormData({ ...formData, supplierName: e.target.value })}
                      placeholder="EDF, Orange, etc."
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-500 mb-1 block uppercase">Contact fournisseur</label>
                    <input
                      type="text"
                      className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white text-slate-900 text-sm"
                      value={formData.supplierContact || ''}
                      onChange={(e) => setFormData({ ...formData, supplierContact: e.target.value })}
                      placeholder="Email ou téléphone"
                    />
                  </div>
                </div>

                {/* Statut */}
                <div>
                  <label className="text-xs font-bold text-slate-500 mb-1 block uppercase">Statut</label>
                  <SearchableSelect
                    options={[
                      { value: 'PENDING', label: 'En attente' },
                      { value: 'PAID', label: 'Payé' },
                      { value: 'CANCELLED', label: 'Annulé' }
                    ]}
                    value={formData.status || 'PENDING'}
                    onChange={(value) => setFormData({ ...formData, status: value as ExpenseStatus })}
                    placeholder="Sélectionner un statut"
                    icon={<Check className="w-4 h-4" />}
                    searchPlaceholder="Rechercher un statut..."
                  />
                </div>
              </div>
            )}
          </div>

          {/* Actions rapides */}
          {!expenseToEdit && formData.expenseType && formData.category && formData.amount > 0 && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
              <p className="text-xs font-bold text-slate-500 mb-2 uppercase">Actions rapides</p>
              <button
                type="button"
                onClick={handleDuplicateForNextMonth}
                className="flex items-center space-x-2 text-sm text-slate-600 hover:text-amber-600 font-medium"
              >
                <Repeat className="w-4 h-4" />
                <span>Dupliquer pour le mois suivant</span>
              </button>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200 sticky bottom-0 bg-white pb-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors font-medium"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium transition-colors shadow-sm"
            >
              {expenseToEdit ? 'Modifier' : 'Créer'} la dépense
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
